import React, {useState, useEffect, useMemo} from "react";
import {Modal, Button, Form} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import Select from "react-select";
import {dashboardAPI} from "../../../services/api";
import {mockStores} from "../../../data/stores";

export const CreateStoreGroup = ({ show, onClose, onSave }: any) => {
    const {t} = useTranslation();
    const [formState, setFormState] = useState({
        name: '',
        description: '',
        active: true
    });
    const [stores, setStores] = useState<any[]>([]);
    const [selectedStores, setSelectedStores] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Check if demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        if (show) {
            // Reset form when modal opens
            setFormState({
                name: '',
                description: '',
                active: true
            });
            setSelectedStores([]);
            setError(null);
            
            // Fetch stores
            setIsLoading(true);
            if (isDemoUser) {
                // Use mock data for demo
                setStores(mockStores);
                setIsLoading(false);
            } else {
                // Fetch stores from API
                dashboardAPI.getStores().then((response: any) => {
                    const storesData = Array.isArray(response) ? response : (response?.data || []);
                    setStores(storesData);
                    setIsLoading(false);
                }).catch((err: any) => {
                    console.error('Failed to fetch stores:', err);
                    setError(err?.message || 'Failed to fetch stores');
                    setIsLoading(false);
                });
            }
        }
    }, [show, isDemoUser]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormState(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async () => {
        // Validation
        setError(null);
        
        if (!formState.name.trim()) {
            setError(t('StoreGroupList.name_required') || 'Store group name is required');
            return;
        }

        if (formState.name.trim().length < 2) {
            setError(t('StoreGroupList.name_min_length') || 'Store group name must be at least 2 characters');
            return;
        }

        if (formState.name.trim().length > 100) {
            setError(t('StoreGroupList.name_max_length') || 'Store group name must not exceed 100 characters');
            return;
        }

        if (formState.description && formState.description.length > 500) {
            setError(t('StoreGroupList.description_max_length') || 'Description must not exceed 500 characters');
            return;
        }

        setIsSubmitting(true);

        try {
            // Create store group
            const storeGroupData = {
                name: formState.name.trim(),
                description: formState.description.trim() || undefined,
                active: formState.active
            };
            
            const response: any = await dashboardAPI.createStoreGroup(storeGroupData);
            // Handle both AxiosResponse and plain object responses
            const createdStoreGroup = (response as any)?.data || response;
            const storeGroupId = createdStoreGroup?.id;
            
            // Assign stores if any selected
            const selectedStoreIds = selectedStores.map(s => s.value);
            if (selectedStoreIds.length > 0 && storeGroupId) {
                if (isDemoUser) {
                    // In demo mode, just log
                    console.log('🔧 Demo: Would assign stores to store group:', selectedStoreIds);
                } else {
                    await dashboardAPI.assignStoresToStoreGroup(storeGroupId, selectedStoreIds);
                }
            }
            
            await onSave();
        } catch (error: any) {
            console.error('Failed to create store group:', error);
            setError(error?.response?.data?.message || error?.message || t('StoreGroupList.create_failed') || 'Failed to create store group');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} backdrop="static" size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('StoreGroupList.create_store_group') || 'Create Store Group'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t('StoreGroupList.name') || 'Name'} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={formState.name}
                            onChange={handleChange}
                            placeholder={t('StoreGroupList.name_placeholder') || 'Enter store group name'}
                            disabled={isSubmitting}
                            maxLength={100}
                        />
                        <Form.Text className="text-muted">
                            {t('StoreGroupList.name_help') || 'Store group name must be unique within your company (2-100 characters)'}
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t('StoreGroupList.description') || 'Description'}
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={formState.description}
                            onChange={handleChange}
                            placeholder={t('StoreGroupList.description_placeholder') || 'Enter description (optional)'}
                            disabled={isSubmitting}
                            maxLength={500}
                        />
                        <Form.Text className="text-muted">
                            {t('StoreGroupList.description_help') || 'Optional description (max 500 characters)'}
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            name="active"
                            checked={formState.active}
                            onChange={handleChange}
                            label={t('StoreGroupList.active') || 'Active'}
                            disabled={isSubmitting}
                        />
                        <Form.Text className="text-muted">
                            {t('StoreGroupList.active_help') || 'Inactive store groups cannot be assigned to users'}
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t('StoreGroupList.assign_stores') || 'Assign Stores'}
                        </Form.Label>
                        {isLoading ? (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <Select
                                isMulti
                                options={stores.map((store: any) => ({
                                    value: store.id,
                                    label: `${store.name || store.id}${store.city ? ` (${store.city})` : ''}`
                                }))}
                                value={selectedStores}
                                onChange={(selected: any) => setSelectedStores(selected || [])}
                                placeholder={t('StoreGroupList.select_stores_placeholder') || 'Select stores to assign to this group...'}
                                isDisabled={isSubmitting}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        )}
                        <Form.Text className="text-muted">
                            {t('StoreGroupList.assign_stores_help') || 'Select one or more stores to assign to this store group'}
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                    {t('Common.cancel') || 'Cancel'}
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('Common.creating') || 'Creating...'}
                        </>
                    ) : (
                        t('Common.create') || 'Create'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

